/**
 * Handwriting Project Service
 *
 * Handles create/update of handwriting projects. Uses useApiFetch for consistent
 * base URL, retries, and error handling. Used by the handwritten-note personalize flow.
 */

import { useApiFetch } from '@/composables/useApiFetch'
import type { RecentHandwritingProject } from '~~/apps/capacitor/types/recentHandwritingProject'
import type {
    HandwritingProjectCancelResponse,
    HandwritingProjectRecord,
} from '~~/layers/base/types/handwritingProject'
import type { HandwritingProjectPatchBody } from '~~/layers/base/utils/handwritingProjectSchemas'

export type CreateHandwritingProjectBody = {
    contact_id?: string | null
    recipient_address_id?: string | null
    return_address_id?: string | null
    message?: string | null
    font?: string | null
}

/** POST returns a full record; the project store may short-circuit with `{ id }` only when reusing an id. */
export type HandwritingProjectCreateResult = HandwritingProjectRecord | { id: string }

export const useHandwritingProjectService = () => {
    /**
     * Create a handwriting project with optional recipient (contact_id, recipient_address_id).
     * Resolves stationery from design_project_id on the server.
     *
     * @param body – design_project_id (required), optional contact_id, recipient_address_id, return_address_id
     * @returns Promise with { data, error, pending, refresh }
     */
    const doCreateHandwritingProject = async (
        body: CreateHandwritingProjectBody,
        options?: { signal?: AbortSignal }
    ) => {
        const result = await useApiFetch<HandwritingProjectRecord>('/api/handwriting-projects', {
            method: 'POST',
            body: {
                contact_id: body.contact_id ?? undefined,
                recipient_address_id: body.recipient_address_id ?? undefined,
                return_address_id: body.return_address_id ?? undefined,
                message: body.message ?? undefined,
                font: body.font ?? undefined,
            },
            signal: options?.signal,
        })
        return result
    }

    /** Fetch one handwriting project row by id for page hydration/editing. */
    const doFetchHandwritingProject = async (id: string) => {
        return useApiFetch<HandwritingProjectRecord>(`/api/handwriting-projects/${id}`, {
            method: 'GET',
        })
    }

    /**
     * Partially update an existing handwriting project row (autosave, preview image, `status_type`).
     */
    const doPatchHandwritingProject = async (id: string, body: HandwritingProjectPatchBody) => {
        return useApiFetch<HandwritingProjectRecord>(`/api/handwriting-projects/${id}`, {
            method: 'PATCH',
            body,
        })
    }

    /**
     * Cancel a handwriting project for the current profile (`POST …/cancel`).
     * Prefer this over PATCH `status_type: 'cancelled'` so server-side refund/tender logic can live on one route.
     * Response `{ id, cancelled }`; `false` means active status was not cancellable (no-op).
     */
    const doCancelHandwritingProject = async (id: string) => {
        return useApiFetch<HandwritingProjectCancelResponse>(
            `/api/handwriting-projects/${id}/cancel`,
            {
                method: 'POST',
            }
        )
    }

    /**
     * Load the most recently updated **submitted** handwriting projects for the signed-in session (home list;
     * excludes projects whose active status is `draft_not_submitted`).
     *
     * @returns Promise with `{ data, error, pending, refresh }` — `data` is `RecentHandwritingProject[]`
     *   (type: `apps/capacitor/types/recentHandwritingProject.ts`), each item including `activeStatus`
     *   for the current `handwriting_project_status` row.
     */
    const doFetchRecentHandwritingProjects = async () => {
        return useApiFetch<RecentHandwritingProject[]>('/api/handwriting-projects/recent', {
            method: 'GET',
        })
    }

    return {
        doCreateHandwritingProject,
        doFetchHandwritingProject,
        doPatchHandwritingProject,
        doCancelHandwritingProject,
        doFetchRecentHandwritingProjects,
    }
}
